import { Fragment, useEffect, useState } from 'react'
import {
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { Link as RouterLink, Navigate } from 'react-router-dom'
import ContentContainer from '../../areas/layout/content-container'
import {
  PageWrapper,
  SettingsWrapper,
  SectionTitle,
  SectionSubtitle,
  RowText,
  RowLabel,
} from '../Profile/profile-styles'
import {
  StatCardsRow,
  StatCardBox,
  TestPushRow,
  OptInHeaderRow,
  OptInCountBox,
  OptInListBox,
  AdminBadgeChip,
  FlagChipStyled,
  CollapsibleCell,
  UserDetailsBox,
  UserNameLink,
  UserMetaText,
  UserActionsRow,
  ScrollableTableContainer,
  MostTrackedColumns,
  MostTrackedColumn,
  MostTrackedHeading,
  MostTrackedRow,
  MostTrackedTitleLink,
  CommentRow,
  CommentBody,
  CommentContent,
  CommentActions,
  CommentMediaLink,
  SectionCard,
} from './admin-page-styles'
import {
  MovieceptionErrorButton,
  MovieceptionPrimaryButton,
} from '../../components/Buttons/movieception-buttons'
import { ConfirmModal } from '../../utils/confirm-modal'
import { ONBOARDING_MODAL_PREVIEW_EVENT } from '../../components/onboarding-modal'
import { CenterBox } from '../../utils/center-box'
import { useAuth } from '../../context/AuthContext'
import { useSnackbar } from '../../context/snackbar-context'
import { parseUtcDate } from '../../utils/parse-utc-date'
import adminApi, {
  ADMIN_EMAIL_TYPES,
  AdminComment,
  AdminMostTrackedResponse,
  AdminOverview,
  AdminUser,
} from '../../client/admin-api'

const ADMIN_USER_TYPE_ID = 2

// Episode comments live on season pages; everything else on the detail page.
const commentMediaPath = (comment: AdminComment) => {
  const type = comment.mediaType.toLowerCase()
  if (type === 'movie') return `/details/movie/${comment.tmdbMediaId}`
  if (type === 'episode' && comment.seasonNumber != null) {
    const base = `/shows/${comment.tmdbMediaId}/season/${comment.seasonNumber}`
    return comment.episodeNumber != null
      ? `${base}?episode=${comment.episodeNumber}`
      : base
  }
  return `/details/show/${comment.tmdbMediaId}`
}

const formatDate = (iso?: string | null) =>
  iso
    ? parseUtcDate(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '-'

const formatDateTime = (iso?: string | null) =>
  iso
    ? parseUtcDate(iso).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '-'

const StatCard = ({ label, value }: { label: string; value: number }) => (
  <StatCardBox>
    <Typography fontSize={24} fontWeight={700}>
      {value}
    </Typography>
    <Typography fontSize={13} color='text.secondary'>
      {label}
    </Typography>
  </StatCardBox>
)

const FlagChip = ({ label, on }: { label: string; on: boolean }) => (
  <FlagChipStyled
    size='small'
    label={label}
    color={on ? 'success' : 'default'}
    variant={on ? 'filled' : 'outlined'}
  />
)

// One email type: count plus an expandable list of exactly which accounts have it on.
const OptInRow = ({
  label,
  matches,
  total,
}: {
  label: string
  matches: AdminUser[]
  total: number
}) => {
  const [open, setOpen] = useState(false)

  return (
    <>
      <OptInHeaderRow onClick={() => setOpen(!open)}>
        <RowText>
          <RowLabel>{label}</RowLabel>
        </RowText>
        <OptInCountBox>
          <Typography fontWeight={600}>
            {matches.length} / {total}
          </Typography>
          {open ? (
            <KeyboardArrowUpIcon fontSize='small' />
          ) : (
            <KeyboardArrowDownIcon fontSize='small' />
          )}
        </OptInCountBox>
      </OptInHeaderRow>
      <Collapse in={open} timeout='auto' unmountOnExit>
        <OptInListBox>
          {matches.length === 0 ? (
            <Typography fontSize={13} color='text.secondary'>
              No accounts have this enabled.
            </Typography>
          ) : (
            matches.map((u) => (
              <Typography key={u.id} fontSize={13} color='text.secondary'>
                {u.name} · {u.emailAddress}
              </Typography>
            ))
          )}
        </OptInListBox>
      </Collapse>
    </>
  )
}

const UserRow = ({
  user,
  onSendEmail,
  onReopen,
}: {
  user: AdminUser
  onSendEmail: (user: AdminUser, type: string) => Promise<void>
  onReopen: (user: AdminUser) => void
}) => {
  const [open, setOpen] = useState(false)
  const [emailType, setEmailType] = useState<string>('welcome')
  const [sending, setSending] = useState(false)

  const handleSend = async () => {
    setSending(true)
    try {
      await onSendEmail(user, emailType)
    } finally {
      setSending(false)
    }
  }
  const status = user.deletedOnUtc
    ? 'Deleted'
    : user.isActive
      ? 'Active'
      : 'Inactive'

  return (
    <Fragment>
      <TableRow hover>
        <TableCell padding='checkbox'>
          <IconButton size='small' onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <UserNameLink
            component={RouterLink}
            to={`/user/${user.username ?? user.id}`}
          >
            {user.name}
          </UserNameLink>
          {user.userTypeId === ADMIN_USER_TYPE_ID && (
            <AdminBadgeChip size='small' label='Admin' color='primary' />
          )}
        </TableCell>
        <TableCell>{user.emailAddress}</TableCell>
        <TableCell>{formatDate(user.createdOnUtc)}</TableCell>
        <TableCell>{formatDateTime(user.lastLoginTimeUtc)}</TableCell>
        <TableCell>
          <Chip
            size='small'
            label={status}
            color={
              status === 'Active'
                ? 'success'
                : status === 'Deleted'
                  ? 'error'
                  : 'default'
            }
            variant='outlined'
          />
        </TableCell>
        <TableCell>
          {user.emailNotifications && user.isSubscribedToEmailNotifications
            ? 'On'
            : 'Off'}
        </TableCell>
        <TableCell>{user.theme}</TableCell>
        <TableCell>{user.region}</TableCell>
      </TableRow>
      <TableRow>
        <CollapsibleCell colSpan={9} open={open}>
          <Collapse in={open} timeout='auto' unmountOnExit>
            <UserDetailsBox>
              <UserMetaText fontSize={13} color='text.secondary'>
                Landing: {user.defaultLandingPage} · Visibility:{' '}
                {user.profileVisibility} · Frequency:{' '}
                {user.notificationFrequency}
              </UserMetaText>
              <FlagChip label='Spoiler control' on={user.spoilerControl} />
              <FlagChip
                label='Progress bars'
                on={user.showProgressIndicators}
              />
              <FlagChip label='Hide TMDB reviews' on={user.hideTmdbReviews} />
              <FlagChip label='Episode alerts' on={user.newEpisodeAlerts} />
              <FlagChip label='Season alerts' on={user.newSeasonAlerts} />
              <FlagChip label='Recommendations' on={user.recommendations} />
              <FlagChip label='New releases' on={user.newReleases} />
              <FlagChip label='Comment replies' on={user.commentReplies} />
              <FlagChip
                label='Activity reminders'
                on={user.activityReminders}
              />
              <FlagChip
                label='Recap & milestones'
                on={user.recapAndMilestones}
              />
              <FlagChip label='Quiet hours' on={user.quietHoursEnabled} />

              <UserActionsRow>
                {user.deletedOnUtc ? (
                  <MovieceptionErrorButton
                    label='Reopen Account'
                    onClick={() => onReopen(user)}
                  />
                ) : (
                  <>
                    <Select
                      size='small'
                      value={emailType}
                      onChange={(e) => setEmailType(e.target.value)}
                      disabled={sending}
                    >
                      {ADMIN_EMAIL_TYPES.map((t) => (
                        <MenuItem key={t.value} value={t.value}>
                          {t.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <MovieceptionPrimaryButton
                      label={sending ? 'Sending…' : 'Send Email'}
                      onClick={handleSend}
                      disabled={sending}
                    />
                    <Typography fontSize={12} color='text.secondary'>
                      Uses the real pipeline - respects their notification
                      settings.
                    </Typography>
                  </>
                )}
              </UserActionsRow>
            </UserDetailsBox>
          </Collapse>
        </CollapsibleCell>
      </TableRow>
    </Fragment>
  )
}

const AdminPage = () => {
  const { user, isAuthenticated, isLoading, getAccessToken } = useAuth()
  const { enqueueSnackbar } = useSnackbar()

  const [overview, setOverview] = useState<AdminOverview | null>(null)
  const [mostTracked, setMostTracked] =
    useState<AdminMostTrackedResponse | null>(null)
  const [users, setUsers] = useState<AdminUser[]>([])
  const [comments, setComments] = useState<AdminComment[]>([])
  const [loading, setLoading] = useState(true)
  const [commentToDelete, setCommentToDelete] = useState<AdminComment | null>(
    null,
  )
  const [userToReopen, setUserToReopen] = useState<AdminUser | null>(null)
  const [reopening, setReopening] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const isAdmin = user?.userTypeId === ADMIN_USER_TYPE_ID

  useEffect(() => {
    // Wait for Auth0 to finish on refresh - the cached user makes isAdmin true
    // before the axios token getter is registered, which would 401 every call.
    if (!isAdmin || !isAuthenticated) return
    const fetchAll = async () => {
      try {
        await getAccessToken()
        const [overviewRes, usersRes, commentsRes, mostTrackedRes] =
          await Promise.all([
            adminApi.getOverview(),
            adminApi.getUsers(),
            adminApi.getRecentComments(50),
            adminApi.getMostTracked(10),
          ])
        setOverview(overviewRes.data)
        setUsers(usersRes.data)
        setMostTracked(mostTrackedRes.data)
        setComments(commentsRes.data)
      } catch {
        enqueueSnackbar('Failed to load admin data.', { variant: 'error' })
      } finally {
        setLoading(false)
      }
    }
    fetchAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, isAuthenticated])

  const handleSendEmail = async (target: AdminUser, type: string) => {
    try {
      await adminApi.sendUserEmail(target.id, type)
      enqueueSnackbar(`Sent "${type}" to ${target.emailAddress}`, {
        variant: 'success',
      })
    } catch {
      enqueueSnackbar('Could not send the email.', { variant: 'error' })
    }
  }

  const [sendingTestPush, setSendingTestPush] = useState(false)
  const handleTestPush = async () => {
    setSendingTestPush(true)
    try {
      await adminApi.sendTestPush()
      enqueueSnackbar('Test push sent to your registered devices.', {
        variant: 'success',
      })
    } catch {
      enqueueSnackbar('Could not send the test push.', { variant: 'error' })
    } finally {
      setSendingTestPush(false)
    }
  }

  const handleReopenUser = async () => {
    if (!userToReopen) return
    setReopening(true)
    try {
      await adminApi.reopenUser(userToReopen.id)
      setUsers((prev) =>
        prev.map((u) =>
          u.id === userToReopen.id
            ? { ...u, isActive: true, deletedOnUtc: null }
            : u,
        ),
      )
      enqueueSnackbar(`Reopened ${userToReopen.emailAddress}`, {
        variant: 'success',
      })
    } catch {
      enqueueSnackbar('Could not reopen the account.', { variant: 'error' })
    } finally {
      setReopening(false)
      setUserToReopen(null)
    }
  }

  const handleDeleteComment = async () => {
    if (!commentToDelete) return
    setDeleting(true)
    try {
      await adminApi.deleteComment(commentToDelete.id)
      setComments((prev) => prev.filter((c) => c.id !== commentToDelete.id))
      enqueueSnackbar('Comment deleted', { variant: 'success' })
    } catch {
      enqueueSnackbar('Could not delete comment.', { variant: 'error' })
    } finally {
      setDeleting(false)
      setCommentToDelete(null)
    }
  }

  if (isLoading) {
    return (
      <ContentContainer>
        <CenterBox>
          <CircularProgress size={48} />
        </CenterBox>
      </ContentContainer>
    )
  }

  // Not signed in or not an admin: nothing to see here.
  if (!isAuthenticated || !isAdmin) {
    return <Navigate to='/' replace />
  }

  // Deleted accounts never receive emails, so opt-ins only look at live ones.
  const activeUsers = users.filter((u) => !u.deletedOnUtc)
  const optInRows: { label: string; test: (u: AdminUser) => boolean }[] = [
    {
      label: 'Subscribed (registration flag)',
      test: (u) => u.isSubscribedToEmailNotifications,
    },
    {
      label: 'Email notifications (master)',
      test: (u) => u.emailNotifications,
    },
    { label: 'New episode alerts', test: (u) => u.newEpisodeAlerts },
    { label: 'New season alerts', test: (u) => u.newSeasonAlerts },
    { label: 'Recommendations', test: (u) => u.recommendations },
    { label: 'New releases', test: (u) => u.newReleases },
    { label: 'Comment replies', test: (u) => u.commentReplies },
    { label: 'Activity reminders', test: (u) => u.activityReminders },
    { label: 'Recap & milestones', test: (u) => u.recapAndMilestones },
    {
      label: 'Weekly frequency',
      test: (u) => u.notificationFrequency?.toLowerCase() === 'weekly',
    },
    { label: 'Quiet hours enabled', test: (u) => u.quietHoursEnabled },
  ]

  return (
    <ContentContainer>
      <PageWrapper>
        <SettingsWrapper>
          <SectionCard>
            <SectionTitle>Admin · Overview</SectionTitle>
            {loading || !overview ? (
              <CircularProgress size={24} />
            ) : (
              <>
                <StatCardsRow>
                  <StatCard label='Total users' value={overview.totalUsers} />
                  <StatCard label='Active users' value={overview.activeUsers} />
                  <StatCard
                    label='Deleted accounts'
                    value={overview.deletedUsers}
                  />
                  <StatCard
                    label='New this month'
                    value={overview.newUsersThisMonth}
                  />
                  <StatCard
                    label='Emails sent (7d)'
                    value={overview.emailsSentLast7Days}
                  />
                  <StatCard
                    label='Email backlog'
                    value={overview.pendingEmailsBacklog}
                  />
                </StatCardsRow>
                <TestPushRow>
                  <MovieceptionPrimaryButton
                    label={
                      sendingTestPush
                        ? 'Sending…'
                        : 'Send Test Push to My Devices'
                    }
                    onClick={handleTestPush}
                    disabled={sendingTestPush}
                  />
                  <MovieceptionPrimaryButton
                    label='Preview Onboarding Modal'
                    onClick={() =>
                      window.dispatchEvent(
                        new Event(ONBOARDING_MODAL_PREVIEW_EVENT),
                      )
                    }
                  />
                </TestPushRow>
              </>
            )}
          </SectionCard>

          <SectionCard>
            <SectionTitle>Email Opt-ins</SectionTitle>
            <SectionSubtitle>
              Active accounts with each email type enabled (out of{' '}
              {activeUsers.length}). Click a row to see which accounts.
            </SectionSubtitle>
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              optInRows.map((row) => (
                <OptInRow
                  key={row.label}
                  label={row.label}
                  matches={activeUsers.filter(row.test)}
                  total={activeUsers.length}
                />
              ))
            )}
          </SectionCard>

          <SectionCard>
            <SectionTitle>Users</SectionTitle>
            <SectionSubtitle>
              All accounts, newest first. Expand a row for preferences.
            </SectionSubtitle>
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              <ScrollableTableContainer>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Joined</TableCell>
                      <TableCell>Last login</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Emails</TableCell>
                      <TableCell>Theme</TableCell>
                      <TableCell>Region</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((u) => (
                      <UserRow
                        key={u.id}
                        user={u}
                        onSendEmail={handleSendEmail}
                        onReopen={setUserToReopen}
                      />
                    ))}
                  </TableBody>
                </Table>
              </ScrollableTableContainer>
            )}
          </SectionCard>

          <SectionCard>
            <SectionTitle>Most Tracked</SectionTitle>
            <SectionSubtitle>
              Top titles by how many users have them in their history.
            </SectionSubtitle>
            {loading || !mostTracked ? (
              <CircularProgress size={24} />
            ) : (
              <MostTrackedColumns>
                {(
                  [
                    {
                      heading: 'Shows',
                      items: mostTracked.shows ?? [],
                      path: 'show',
                    },
                    {
                      heading: 'Movies',
                      items: mostTracked.movies ?? [],
                      path: 'movie',
                    },
                  ] as const
                ).map((col) => (
                  <MostTrackedColumn key={col.heading}>
                    <MostTrackedHeading>{col.heading}</MostTrackedHeading>
                    {col.items.length === 0 ? (
                      <Typography fontSize={13} color='text.secondary'>
                        Nothing tracked yet.
                      </Typography>
                    ) : (
                      col.items.map((item, index) => (
                        <MostTrackedRow key={item.tmdbMediaId}>
                          <MostTrackedTitleLink
                            component={RouterLink}
                            to={`/details/${col.path}/${item.tmdbMediaId}`}
                          >
                            {index + 1}. {item.title}
                          </MostTrackedTitleLink>
                          <Typography fontSize={14} color='text.secondary'>
                            {item.userCount}{' '}
                            {item.userCount === 1 ? 'user' : 'users'}
                          </Typography>
                        </MostTrackedRow>
                      ))
                    )}
                  </MostTrackedColumn>
                ))}
              </MostTrackedColumns>
            )}
          </SectionCard>

          <SectionCard>
            <SectionTitle>Recent Comments</SectionTitle>
            <SectionSubtitle>
              Latest 50 user comments across all media.
            </SectionSubtitle>
            {loading ? (
              <CircularProgress size={24} />
            ) : comments.length === 0 ? (
              <Typography color='text.secondary'>No comments yet.</Typography>
            ) : (
              comments.map((comment) => (
                <CommentRow key={comment.id}>
                  <CommentBody>
                    <Typography fontSize={13} color='text.secondary'>
                      {comment.authorName} ·{' '}
                      <CommentMediaLink
                        component={RouterLink}
                        to={commentMediaPath(comment)}
                      >
                        {comment.mediaType} #{comment.tmdbMediaId}
                      </CommentMediaLink>
                      {comment.parentCommentId ? ' · reply' : ''} ·{' '}
                      {formatDate(comment.createdOnUtc)}
                    </Typography>
                    <CommentContent>{comment.content}</CommentContent>
                  </CommentBody>
                  <CommentActions>
                    <MovieceptionErrorButton
                      label='Delete'
                      onClick={() => setCommentToDelete(comment)}
                    />
                  </CommentActions>
                </CommentRow>
              ))
            )}
          </SectionCard>
        </SettingsWrapper>
      </PageWrapper>

      <ConfirmModal
        open={userToReopen !== null}
        title='Reopen Account'
        description={`Reopen ${userToReopen?.emailAddress}? The user will be able to sign in again immediately; their data was never removed.`}
        confirmText={reopening ? 'Reopening…' : 'Reopen'}
        confirmDisabled={reopening}
        onConfirm={handleReopenUser}
        onCancel={() => setUserToReopen(null)}
      />
      <ConfirmModal
        open={commentToDelete !== null}
        title='Delete Comment'
        description={`Delete ${commentToDelete?.authorName}'s comment? The user will not be notified.`}
        warning="This can't be undone."
        confirmText={deleting ? 'Deleting…' : 'Delete'}
        danger
        confirmDisabled={deleting}
        onConfirm={handleDeleteComment}
        onCancel={() => setCommentToDelete(null)}
      />
    </ContentContainer>
  )
}

export default AdminPage
